import {
  ArrowRight,
  Banknote,
  Hourglass,
  PhoneCall,
  PieChart,
  ShieldAlert,
  ShieldCheck,
  TrendingDown,
  X,
  type LucideIcon,
} from 'lucide-react'

import type { HomeNbaAction, HomeNbaIconKey } from '../../contracts'
import { CoverageBadge } from './coverage'
import { HoverGlowCard } from '../ui/HoverGlowCard'
import { IconButton } from '../ui/IconButton'

import styles from './HomeNbaCard.module.scss'

/**
 * One card in the homepage NBA grid.
 *
 * Two render modes derived from the action payload:
 *
 * - **Singleton**: when `action.singleton_client` is set, the card names a
 *   specific client and frames the body as a moment.
 * - **Cohort**: when `singleton_client` is null, the card names the cohort
 *   and the body carries a count.
 *
 * The selector layer in `services/home_nba/selector.py` decides mode,
 * coverage, and bucket-intrinsic tone — this component owns the
 * "only the leading visible card may be primary" rule via `position`,
 * since position 0 can change when a reserve card is promoted after the
 * banker dismisses a visible card.
 *
 * Click pushes `action.prompt` into the chat through `onClick`. The
 * hover-only X button calls `onDismiss(action.bucket_code)`, which the
 * route uses to drop the card and promote the next-best from the reserve
 * pool. Both interactions are optional; omitting them makes the card a
 * read-only tile.
 */
export function HomeNbaCard({
  action,
  position = 0,
  isExiting = false,
  onClick,
  onDismiss,
}: {
  action: HomeNbaAction
  /** Index of this card in the visible grid (0 = leading). */
  position?: number
  /** Drives the dismiss exit animation; the slot-swap is committed in
   * `HomeRoute` only after the exit animation finishes. */
  isExiting?: boolean
  onClick?: (prompt: string) => void
  onDismiss?: (bucketCode: string) => void
}) {
  const Icon = ICON_FOR_KEY[action.icon_key] ?? PhoneCall
  const isActionable = Boolean(onClick && action.prompt) && !isExiting
  const canDismiss = Boolean(onDismiss) && !isExiting
  // The leading card is the only one that may render in 'primary' tone;
  // positions 2 and 3 always render neutral so the visual rhythm holds
  // even after a dismiss-and-promote.
  const effectiveTone = position === 0 ? action.card_tone : 'neutral'
  const surfaceBackground =
    effectiveTone === 'primary'
      ? 'var(--bank-color-sentiment-positive-soft, var(--card))'
      : 'var(--card)'

  const handleClick = () => {
    if (!isActionable) return
    onClick?.(action.prompt)
  }

  const handleDismissClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Keep the dismiss isolated from the card's open action.
    event.stopPropagation()
    onDismiss?.(action.bucket_code)
  }

  return (
    <HoverGlowCard
      variant="primary"
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      aria-label={isActionable ? `Apri: ${action.title}` : undefined}
      className={`${styles.card} group ${isActionable ? 'cursor-pointer' : ''} h-full transition-opacity duration-500`}
      innerClassName="flex min-h-44 h-full flex-col p-5"
      surfaceBackground={surfaceBackground}
      data-ftue-step={action.ftue_anchor ?? undefined}
      data-bucket={action.bucket_code}
      data-exiting={isExiting ? 'true' : undefined}
      onClick={isActionable ? handleClick : undefined}
      onKeyDown={
        isActionable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleClick()
              }
            }
          : undefined
      }
    >
      {canDismiss ? (
        <IconButton
          size="xs"
          className={styles.closeButton}
          aria-label="Rimuovi questa azione"
          onClick={handleDismissClick}
          onKeyDown={(event) => {
            // IconButton renders a native <button> that handles Enter/Space
            // for activation; we only stop the keydown from bubbling so the
            // parent card's Enter/Space handler does not also fire.
            if (event.key === 'Enter' || event.key === ' ') {
              event.stopPropagation()
            }
          }}
        >
          <X aria-hidden />
        </IconButton>
      ) : null}
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className={styles.kindLabel}>
          <Icon className={styles.kindIcon} aria-hidden />
          <span className={styles.kindChip}>{action.kind_label}</span>
        </span>
      </div>
      <h3 className="text-section-title text-foreground">{action.title}</h3>
      <p className={`mt-2 text-body-default text-muted-foreground ${styles.bodyText}`}>
        {action.body_text}
      </p>
      <div className="mt-auto flex items-end justify-between gap-4 pt-6">
        <span className={styles.metaRow}>
          <span className="text-caption text-muted-foreground">{action.meta_text}</span>
          {action.coverage_status === 'parziale' ? (
            <CoverageBadge status="parziale" note="Alcuni dati sono ancora provvisori." />
          ) : null}
        </span>
        <ArrowRight
          className="size-4 text-muted-foreground transition-colors duration-200 group-hover:text-[color:var(--bank-color-sentiment-positive)]"
          aria-hidden
        />
      </div>
    </HoverGlowCard>
  )
}

/**
 * Skeleton placeholder rendered while the home NBA payload loads. Holds
 * the same min-height as the loaded card so the grid does not shift.
 */
export function HomeNbaCardSkeleton() {
  return (
    <HoverGlowCard
      variant="primary"
      className={`${styles.skeleton} h-full`}
      innerClassName="flex min-h-44 h-full flex-col p-5"
      surfaceBackground="var(--card)"
      aria-hidden
    >
      <div className={styles.skeletonChip} />
      <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`} />
      <div className={styles.skeletonLine} />
      <div className={styles.skeletonFoot} />
    </HoverGlowCard>
  )
}

// ---------------------------------------------------------------------------
// Icon map. Each HomeNbaIconKey resolves to a lucide icon. New buckets need
// a new entry here; falls back to PhoneCall if a backend key is unknown
// (forward-compatibility for new buckets shipped without a frontend rev).
// ---------------------------------------------------------------------------

const ICON_FOR_KEY: Record<HomeNbaIconKey, LucideIcon> = {
  kyc: ShieldCheck,
  credit_event: ShieldAlert,
  concentration: PieChart,
  drift: TrendingDown,
  stewardship: PhoneCall,
  cold_list: PhoneCall,
  maturity: Hourglass,
  idle_liquidity: Banknote,
}
